import { useMemo, useState, useEffect, useRef } from "react"
import { Alert, Button, CloseButton, Group, Text } from "@mantine/core"
import { IconArrowBackUp, IconInfoCircle } from "@tabler/icons-react"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { useStopImpersonation } from "@/features/auth/hooks/auth.queries"

interface ImpersonationBannerProps {
  onHeightChange?: (height: number) => void
}

export const ImpersonationBanner = ({ onHeightChange }: ImpersonationBannerProps) => {
  const { user, hasRole } = useAuth()
  const { mutate: stopImpersonation, isPending } = useStopImpersonation()
  const [closed, setClosed] = useState(false)

  const isAdmin = useMemo(() => {
    if (hasRole("admin")) return true
    if (user?.originalUser?.roles?.includes("admin")) return true

    const microsoftUserJson = localStorage.getItem("microsoft_user")
    if (microsoftUserJson) {
      try {
        const microsoftUser = JSON.parse(microsoftUserJson)
        if (microsoftUser.roles?.includes("admin")) return true
      } catch {
        return false
      }
    }
    return false
  }, [hasRole])

  const originalUser = useMemo(() => {
    if (user?.originalUser) return user.originalUser

    // Backup: check local storage for microsoft session
    const microsoftUserJson = localStorage.getItem("microsoft_user")
    if (microsoftUserJson) {
      try {
        const microsoftUser = JSON.parse(microsoftUserJson)
        const microsoftUserId = microsoftUser.id || microsoftUser._id
        // If current user is different from microsoft user, we are impersonating
        if (microsoftUserId && microsoftUserId !== user?.id) {
          return {
            id: microsoftUserId,
            username: microsoftUser.username,
            email: microsoftUser.email,
            roles: microsoftUser.roles || []
          }
        }
      } catch {
        return null
      }
    }
    return null
  }, [user])

  // Si el usuario original es el mismo que el usuario actual, no hay impersonación real
  const isSelfImpersonation = !!originalUser && (originalUser.id === user?.id || (!!originalUser.username && originalUser.username === user?.username))

  const isVisible = !!originalUser && !isSelfImpersonation && isAdmin && !closed

  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isVisible) onHeightChange?.(0)
  }, [isVisible, onHeightChange])

  useEffect(() => {
    if (!isVisible || !rootRef.current) return

    const element = rootRef.current
    const reportHeight = () => onHeightChange?.(element.offsetHeight)

    reportHeight()
    const observer = new ResizeObserver(reportHeight)
    observer.observe(element)
    return () => observer.disconnect()
  }, [isVisible, onHeightChange])

  // Reset closed state when user changes (new impersonation session)
  useEffect(() => {
    setClosed(false)
  }, [user?.id])

  if (!isVisible) return null

  return (
    <Alert
      ref={rootRef}
      variant="filled"
      color="orange"
      icon={<IconInfoCircle size={16} />}
      radius={0}
      styles={{
        root: { padding: "4px 16px", minHeight: "40px" },
        wrapper: { alignItems: "center" },
        message: { width: "100%" }
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={700}>
            Sesión de Impersonación Activa:
          </Text>
          <Text size="sm">
            Estás como <b>"{user?.username}"</b> (Original: <b>"{originalUser.username}"</b>)
          </Text>
        </Group>
        <Group gap="xs" wrap="nowrap">
          <Button variant="white" color="orange" size="compact-xs" leftSection={<IconArrowBackUp size={14} />} onClick={() => stopImpersonation()} loading={isPending}>
            Regresar
          </Button>
          <CloseButton size="sm" variant="transparent" color="white" onClick={() => setClosed(true)} />
        </Group>
      </Group>
    </Alert>
  )
}
